import { useMutation } from '@apollo/client';
import type { DataImportStartImportMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { DataImportStartImportDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import { useDataImportStartImportTRPC } from './useDataImportStartImportTRPC';

/**
 * Flag-gated wrapper for the `dataImportStartImport` mutation. Defaults to
 * the legacy Apollo mutation and delegates to the tRPC variant when the
 * `trpc` feature flag is enabled. Both paths return the same
 * `DataImportStartImportMutation` shape.
 *
 * CACHE ASYMMETRY (deliberate, documented exception to the web CLAUDE.md
 * cache-parity rule): the legacy Apollo path performs no cache eviction —
 * mirroring the previous inline `useMutation` usage — because the results
 * page tracks import status via an Apollo subscription that keeps those reads
 * live. The tRPC twin (`useDataImportStartImportTRPC`) instead invalidates the
 * `dataImports` and `dataImportById` query families in its `onSuccess`,
 * because TanStack Query has no equivalent subscription keeping those reads
 * fresh.
 *
 * NOTE: the tRPC path is ASYNC — it resolves as soon as the import pipeline
 * has been kicked off (status moves to 'initiatingImport' and the results
 * page's status subscription shows live progress), whereas the legacy Hasura
 * action ran synchronously. Consumers must not assume the import is complete
 * when the promise resolves.
 */
export const useDataImportStartImport = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [startImportGraphQL, graphqlState] = useMutation(
    DataImportStartImportDocument
  );

  const trpcMutation = useDataImportStartImportTRPC();

  const dataImportStartImport = async (
    id: string
  ): Promise<DataImportStartImportMutation> => {
    if (trpcEnabled) {
      return trpcMutation.dataImportStartImport(id);
    }

    const result = await startImportGraphQL({ variables: { Id: id } });
    if (!result.data) {
      throw new Error('Failed to start data import');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      dataImportStartImport,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    dataImportStartImport,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
